import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import type { EditRequest } from '@/types/edit-request';

interface ListPage<T> {
  data: T[];
  meta?: { cursor?: string | null; has_more?: boolean | null };
}

async function fetchAll<T>(path: string): Promise<T[]> {
  const rows: T[] = [];
  let cursor: string | undefined;
  do {
    const params: Record<string, string> = { limit: '100', include_deleted: 'true' };
    if (cursor) params.cursor = cursor;
    const page = await api.get<ListPage<T>>(path, { params });
    rows.push(...page.data);
    cursor = page.meta?.has_more ? (page.meta.cursor ?? undefined) : undefined;
  } while (cursor);
  return rows;
}

// original_data carries cycle_id but no pond, so map cycles to pond names from one cycles list + one ponds list per farm.
export function useTargetPondName(request: EditRequest): string | null {
  const farmId = request.farm_id;
  const cycleId =
    typeof request.original_data.cycle_id === 'string' ? request.original_data.cycle_id : null;

  const { data: pondNames } = useQuery({
    queryKey: qk.pondNames.byCycle(farmId),
    queryFn: async () => {
      const [cycles, ponds] = await Promise.all([
        fetchAll<{ id: string; pond_id: string }>(`/api/v1/farms/${farmId}/cycles`),
        fetchAll<{ id: string; name: string }>(`/api/v1/farms/${farmId}/ponds`),
      ]);
      const nameByPond = new Map(ponds.map((pond) => [pond.id, pond.name]));
      const nameByCycle: Record<string, string> = {};
      for (const cycle of cycles) {
        const name = nameByPond.get(cycle.pond_id);
        if (name) nameByCycle[cycle.id] = name;
      }
      return nameByCycle;
    },
  });

  return (cycleId && pondNames?.[cycleId]) || null;
}
